// SEO helpers shared by the static-rendered pages (home, events, tours,
// articles). Web-only concern: `expo-router/head` is a no-op on native, so
// these values only ever reach an HTML <head> during the `output: "static"`
// web export.

import type { PortableTextBlock } from '@portabletext/react';

import { SITE } from '@site';

import type { SiteConfig } from './site-config';

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from './i18n/locales';
import { usableImageUrl } from './image-url';

// Absolute origin baked into canonical / og:url tags (see src/site.ts).
export const SITE_URL = SITE.url;

export const SITE_NAME = SITE.name;

/** Join a locale + route path onto the site origin, e.g. `absoluteUrl('fr', 'events/x')`. */
export function absoluteUrl(locale: Locale, path = ''): string {
  const clean = path.replace(/^\/+|\/+$/g, '');
  return `${SITE_URL}/${locale}${clean ? `/${clean}` : ''}`;
}

export type HreflangLink = { hrefLang: string; href: string };

/** `hreflang` alternates for every supported locale + `x-default`, for one route path. */
export function hreflangLinks(path = ''): HreflangLink[] {
  return [
    ...SUPPORTED_LOCALES.map((locale) => ({ hrefLang: locale, href: absoluteUrl(locale, path) })),
    { hrefLang: 'x-default', href: absoluteUrl(DEFAULT_LOCALE, path) },
  ];
}

// Keyed by every language a site may enable, not just this site's.
const OG_LOCALE: Record<string, string> = { en: 'en_US', fr: 'fr_FR', de: 'de_DE', es: 'es_US', ru: 'ru_RU' };

export function ogLocale(locale: Locale): string {
  return OG_LOCALE[locale];
}

/** `og:locale:alternate` values — every supported locale except the current one. */
export function ogLocaleAlternates(locale: Locale): string[] {
  return SUPPORTED_LOCALES.filter((l) => l !== locale).map((l) => OG_LOCALE[l]);
}

/**
 * Longest `<title>` that survives intact in a Google result. Past this the tail
 * is replaced with an ellipsis, so anything beyond it is wasted — and on this
 * site it's the differentiating part that gets cut, since Viator product names
 * front-load generic words ("Private Sightseeing Tour …").
 */
const MAX_TITLE_LENGTH = 60;

/**
 * `<title>` text for a detail page, fitted to the SERP budget by dropping the
 * least important part first.
 *
 * Order of sacrifice: the brand suffix goes before the name is touched, and
 * `qualifier` (an event's date) is never trimmed — it's what distinguishes the
 * 393 pages that shared a title with another page, because recurring events
 * arrive from the feed as one row per date under the same name. The full,
 * untrimmed name always stays in the page's <h1>.
 *
 * Viator names reach 128 characters, so tours are usually the ones that lose
 * the suffix; most event names fit with it.
 */
export function seoTitle(name: string, qualifier?: string): string {
  const tail = qualifier ? ` — ${qualifier}` : '';
  const withBrand = `${name}${tail} — ${SITE_NAME}`;
  if (withBrand.length <= MAX_TITLE_LENGTH) return withBrand;

  const bare = `${name}${tail}`;
  if (bare.length <= MAX_TITLE_LENGTH) return bare;

  return `${truncate(name, Math.max(MAX_TITLE_LENGTH - tail.length, 24))}${tail}`;
}

/** Trim to a meta-description-friendly length on a word boundary. */
export function truncate(text: string, max = 160): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const slice = clean.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(' ');
  return `${(lastSpace > 40 ? slice.slice(0, lastSpace) : slice).trimEnd()}…`;
}

/**
 * Width a CDN URL *declares* in its path or query, or null when it doesn't say.
 *
 * Feed images arrive at whatever size the partner publishes and we can't
 * upscale them: the AWIN/Fnac datafeed only ever ships `…_222x222.jpg` (its
 * `aw_image_url` alternative is a 200x200 proxy of the same file, and every
 * other size suffix 404s), while Viator/TripAdvisor ships `…-720x480/…`.
 * Google wants >=696px for image rich results, so `richResultImage()` below
 * uses this to drop the ones it would reject rather than declaring an image
 * that fails validation.
 */
export function declaredImageWidth(url: string): number | null {
  // `…_222x222.jpg`, `…-720x480/…` — dimensions baked into the path.
  const path = url.match(/[_-](\d{2,4})x(\d{2,4})(?:[./]|$)/);
  if (path) return Number(path[1]);
  // `?w=200`, `&w=1600` — dimensions as a query param.
  const query = url.match(/[?&]w=(\d{2,4})\b/);
  if (query) return Number(query[1]);
  return null;
}

/** Google's minimum width for an image to be eligible for rich results. */
const RICH_RESULT_MIN_WIDTH = 696;

/**
 * The URL, or null when it's demonstrably too small for a rich result. Sizes we
 * can't determine are passed through — better to let Google judge than to drop
 * a usable image on a false negative.
 */
export function richResultImage(url: string | null | undefined): string | null {
  url = usableImageUrl(url);
  if (!url) return null;
  const width = declaredImageWidth(url);
  return width != null && width < RICH_RESULT_MIN_WIDTH ? null : url;
}

/**
 * Google's Merchant-listing enrichment treats any Product + priced Offer as a
 * shoppable listing and flags `shippingDetails` / `hasMerchantReturnPolicy` as
 * missing (Search Console "Merchant listings" report, issue #22). Tours are
 * booked experiences delivered as an electronic voucher — nothing ships and
 * nothing can be sent back — so the honest declaration is a zero-rate
 * shipping entry and an explicit "returns not permitted" policy, rather than
 * leaving both fields off and keeping the warning. Country is SITE.country: the
 * site's audience and every supplier's billing region. The voucher / booking
 * confirmation arrives at once, so `deliveryTime` is zero days handling + zero
 * days transit (Google flags it as missing otherwise). Used for tours and
 * boat rentals alike. See docs/seo-structured-data.md.
 */
export function offerShippingDetails(currency: string) {
  const sameDay = { '@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY' };
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: { '@type': 'MonetaryAmount', value: 0, currency },
    shippingDestination: { '@type': 'DefinedRegion', addressCountry: SITE.country },
    deliveryTime: { '@type': 'ShippingDeliveryTime', handlingTime: sameDay, transitTime: sameDay },
  };
}

/** See offerShippingDetails(). */
export function merchantReturnPolicy() {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: SITE.country,
    returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
  };
}

/**
 * schema.org/Organization for the site itself — emitted once per page, in the
 * app's [lang] layout (it needs the locale for the description). `logo` is the
 * site's mark rendered to public/logo-512.png.
 */
export function organizationJsonLd(locale: Locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo-512.png`,
      width: 512,
      height: 512,
    },
    description: SITE.description[locale],
    areaServed: {
      '@type': 'Place',
      name: SITE.regionName[locale],
    },
  };
}

/** schema.org/WebSite — ties the locale trees together under one site entity. */
export function webSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: [...SUPPORTED_LOCALES],
    publisher: { '@id': `${SITE_URL}/#organization` },
  };
}

export type Crumb = { name: string; path?: string };

// Widened to the SiteConfig shape: optional crumbs (wineTours) are absent from
// some sites' SITE literal.
const CRUMBS: SiteConfig['crumbs'] = SITE.crumbs;

/**
 * schema.org/BreadcrumbList for a detail page. The home crumb is always first;
 * pass the intermediate crumbs a page actually has. The `events` crumb exists
 * only on a site with an /events hub in its trail (SITE.crumbs.events — dubai);
 * riviera's show pages pass none, and a breadcrumb must never point at a page
 * the site doesn't have.
 */
export function breadcrumbJsonLd(locale: Locale, trail: Crumb[]) {
  const items = [{ name: SITE_NAME, path: '' }, ...trail];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      // The last crumb is the current page: schema.org says to omit `item`
      // there, so only ancestors carry a URL.
      ...(crumb.path != null ? { item: absoluteUrl(locale, crumb.path) } : {}),
    })),
  };
}

/** The `tours` breadcrumb trail entry, localized. */
export function toursCrumb(locale: Locale): Crumb {
  return { name: CRUMBS.tours[locale], path: 'tours' };
}

/** The `wine-tours` breadcrumb trail entry, localized. */
export function wineToursCrumb(locale: Locale): Crumb {
  return { name: CRUMBS.wineTours?.[locale] ?? CRUMBS.tours[locale], path: 'wine-tours' };
}

/** The `articles` breadcrumb trail entry, localized. */
export function articlesCrumb(locale: Locale): Crumb {
  return { name: CRUMBS.articles[locale], path: 'articles' };
}

/** The `events` breadcrumb trail entry, localized — only where SITE.crumbs.events is set. */
export function eventsCrumb(locale: Locale): Crumb {
  return { name: CRUMBS.events?.[locale] ?? 'Events', path: 'events' };
}

/** The `boats` breadcrumb trail entry, localized. */
export function boatsCrumb(locale: Locale): Crumb {
  return { name: CRUMBS.boats[locale], path: 'boats' };
}

/**
 * schema.org/FAQPage for an on-page FAQ block — boatFaq() in boat-content.ts,
 * wineFaq() in wine-content.ts.
 */
export function faqPageJsonLd(entries: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.q,
      acceptedAnswer: { '@type': 'Answer', text: entry.a },
    })),
  };
}

export type ItemListEntry = { name: string; url: string };

/**
 * schema.org/ItemList for a hub page's listing — marks the page as a hub and
 * hands Google the member URLs directly, independent of whether it renders
 * the page's JS. Shared by the events/tours/articles hubs and the city
 * landing page's own events/tours sections, which all built the exact same
 * `{'@type':'ListItem', position, name, url}` shape independently before this.
 */
export function itemListJsonLd(entries: ItemListEntry[], url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url,
    numberOfItems: entries.length,
    itemListElement: entries.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      url: entry.url,
    })),
  };
}

/** schema.org/Place for a city landing page — ties the page to a real place. */
export function placeJsonLd(name: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name,
    url,
    containedInPlace: { '@type': 'AdministrativeArea', name: SITE.regionName[SITE.defaultLocale] },
  };
}

/** Flatten Portable Text blocks to plain prose for a meta description. */
export function portableTextToPlain(blocks: PortableTextBlock[] | null | undefined): string {
  if (!blocks) return '';
  return blocks
    .filter((b) => b?._type === 'block' && Array.isArray((b as { children?: unknown }).children))
    .map((b) =>
      ((b as { children: { text?: string }[] }).children ?? [])
        .map((c) => c.text ?? '')
        .join(''),
    )
    .join(' ')
    .trim();
}
